/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import clx from 'clsx';
import {
    Box, Button, CircularProgress, Container, FormControl, FormControlLabel, FormGroup, Grid, Table, TableBody,
    TableCell, TableContainer, TableRow, TextField, Typography, Tab, Tabs,
    AccordionDetails, AccordionSummary, Tooltip, Link, Chip, Divider, Select, MenuItem
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TabPanel } from "../../../shared/Tab/TabPanel";
import classNames from "classnames";
import Joi from "joi";
import Settings from '../../../../conf/settings';
import {
    ApplicationKeyReducer, ApplicationReducer, GrantType, ApplicationState, Key, UserReducer, IDPReducer,
    KeyType, ApplicationKeyMapping, IDP, IdpRegisterType, DevPortalAppEnv,
    ApiKeyV2,
    ApiKeysReducer
} from '../../../../data/interfaces';
import {
    fetchAppKeys, createAppKeys, generateApplicationToken, updateApplicationKey, fetchIDPs, fetchSubscriptions, mapApplicationKeys,
    generateToken,
    fetchApiKeysList
} from '../../../../data/operations';
import CopyTextField from '../../../../elements/CopyTextField';
import GreyOutlinedButton from '../../../../elements/GreyOutlinedButton';
import PasswordCopyField from '../../../../elements/PasswordCopyField';
import { PreLoaderLogo } from "../../../../elements/PreLoaderLogo";
import CustomIcons from '../../../../icons/CustomIcons';
import { setErrorNotification } from '../../../../store/actions/notification';
import AppInfoBar from '../../../shared/AppInfoBar';
import { CustomCheckbox, useStyles, NoKeysAccordion, WithKeysAccordion } from './style';
import { createOauthTokenSuccess } from "../../../../store/actions/applicationKeys";
import AccessTokenDialog from "../../../shared/AccessTokenDialog/AccessTokenDialog";
import CurlDialog from "../../../shared/CurlDialog/CurlDialog";
import ExternalIDP from "./ExternalIDP"
import {trackAppProdKeyGeneration, trackAppTestKeyGeneration} from "../../../../util/app-insights";
import WarningBanner from '../../../shared/WarningBanner';
import InfoBanner from '../../../shared/InfoBanner';
import {
    ASGARDEO_CLIENT_ID_DOC_URL,
    AUTHORIZATION_CODE_GRANT_TYPE,
    AZURE_CLIENT_ID_DOC_URL,
    AZURE_KEY_MANAGER_TYPE,
    ASGARDEO_KEY_MANAGER_TYPE,
    DEFAULT_ASGARDEO_KEY_MANAGER_PREFIX,
    IMPLICIT_GRANT_TYPE, 
    RESOURCE_AUTHORIZE, 
    RESOURCE_TOKEN,
    APPDEV_STS_KEY_MANAGER_TYPE, 
    RESOURCE_OAUTH2, 
    RESOURCE_REVOKE
} from "../../../../data/constants";
import InfoTooltip from '../../../../elements/CustomTooltip/InfoTooltip';
import Label from '../../../shared/Label';
import { ApplicationPermission } from '../../../../util/app-permissions';
import { AppPermission } from '../../../../data/emus';
import { ClientIdConfirmationDialog } from './ClientIdConfirmationDialog';
import OutlineButton from '../../../../elements/OutlineButton';
import ApiKeysList, { ViewType } from '../../../shared/ApiKeys/List';
import Loader from '../../../shared/ApiKeys/Loader';

interface OauthKeyProps {
    applicationReducer: ApplicationReducer,
    applicationKeysReducer: ApplicationKeyReducer,
    apiKeysReducer: ApiKeysReducer,
    userReducer: UserReducer,
    idpReducer: IDPReducer,
    keyType: KeyType,
    apiKeys: ApiKeyV2[],
    dispatchGenerateApplicationToken: (applicationId: string, selectedScopes: string[], key: Key) => void,
    dispatchGenerateAppKey: (applicationId: string, organizationId: string, grantTypes: string[], callbackUrl: string,
        additionalProperties: Record<string, unknown>, keyType: KeyType, keyManager: string) => void,
    dispatchFetchAppKeys: (applicationId: string, organizationId: string) => void,
    dispatchUpdateAppKey: (applicationId: string, key: Key) => void,
    dispatchFetchIDPs: (organizationId: string, devPortalAppEnv?: DevPortalAppEnv) => void,
    dispatchSetApplicationKeyMap: (applicationId: string, payload: ApplicationKeyMapping) => void,
    dispatchFetchApiKeys: (apiId: string, applicationId: string, organizationId: string, keyType: string) => void,
}

const idpDocRefFromType = (idpType: string) => {
  switch (idpType) {
    case ASGARDEO_KEY_MANAGER_TYPE:
      return ASGARDEO_CLIENT_ID_DOC_URL;
    case AZURE_KEY_MANAGER_TYPE:
      return AZURE_CLIENT_ID_DOC_URL;
    default:
      return null;
  }
};

function ShowClientIDDocumentBanner(props: { idpType?: string }) {
  const { idpType } = props;
  if (!idpType) {
    return null;
  }
  const docLink = idpDocRefFromType(idpType);
  if (!docLink) {
    return null;
  }
  const classes = useStyles();
  return (
    <Box mt={2} className={classes.docInfo}>
      <Box>
        <Typography variant="body2" className={classes.docsInformation}>
          <FormattedMessage
            id="app.appProductionKeys.oauth.appkey.documentation.info"
            defaultMessage="Refer to the {documentation} to learn how to add a client ID to the application."
            values={{
              documentation: (
                <Link href={docLink} target="_blank">
                  <FormattedMessage
                    id="app.appProductionKeys.oauth.appkey.documentation.text"
                    defaultMessage="documentation"
                  />
                </Link>
              ),
            }}
          />
        </Typography>
      </Box>
    </Box>
  );
}

function OauthKey(props: OauthKeyProps) {
    const classes = useStyles();
    const intl = useIntl();
    const dispatch = useDispatch();
    const {
        applicationKeysReducer,
        apiKeysReducer,
        applicationReducer,
        idpReducer,
        keyType,
        userReducer,
        dispatchFetchAppKeys,
        dispatchFetchIDPs,
        dispatchGenerateAppKey,
        dispatchUpdateAppKey,
        dispatchSetApplicationKeyMap,
        dispatchFetchApiKeys
    } = props;
    const appList = applicationReducer?.applications?.list;
    const application = appList?.length > 0 ? appList[0] : undefined;
    const applicationId = application?.applicationId;
    const applicationKeys = applicationKeysReducer?.keys;
    const apiKeys = apiKeysReducer?.apiKeys;
    const [currentKey, setCurrentKey] = useState<Key>();
    const isCredentialsAvailable = currentKey !== null && currentKey !== undefined;
    const additionalProperties = currentKey?.additionalProperties;
    const isLoading = applicationKeysReducer.isLoading || idpReducer.isLoading || apiKeysReducer.isLoading;
    const [isTokenGenerating, setIsTokenGenerating] = useState(false);
    const { isKeyMapping, isKeyMappingSuccess, isKeyUpdating } = applicationKeysReducer;
    const [curlOpen, setCurlOpen] = useState(false);
    const [tokenOpen, setTokenOpen] = useState(false);
    const [callbackUrl, setCallbackUrl] = useState(isCredentialsAvailable && currentKey ? currentKey.callbackUrl : "");
    const [token, setToken] = useState('');
    const [isPublicClientEnabled, setIsPublicClientEnabled] = useState<boolean>(isCredentialsAvailable ?
        additionalProperties?.bypassClientCredentials as boolean : false);
    const [isPkceEnabled, setIsPkceEnabled] = useState<boolean>(isCredentialsAvailable ?
        additionalProperties?.pkceMandatory as boolean : false);
    const CLIENT_CREDENTIALS_GRANT_TYPE = 'client_credentials';
    const TOKEN_EXCHANGE_GRANT_TYPE = 'urn:ietf:params:oauth:grant-type:token-exchange';
    const REFRESH_TOKEN_GRANT_TYPE = 'refresh_token';
    const [isGenerateTokenDisabled, setIsGenerateTokenDisabled] = useState(false);
    const [applicationTokenExpiryTime, setApplicationTokenExpiryTime] = useState(isCredentialsAvailable ?
        additionalProperties?.application_access_token_expiry_time : '900');
    const [refreshTokenExpiryTime, setRefreshTokenExpiryTime] = useState(isCredentialsAvailable ?
        additionalProperties?.refresh_token_expiry_time : '86400');
    const [idTokenExpiryTime, setIdTokenExpiryTime] = useState(isCredentialsAvailable ?
        additionalProperties?.id_token_expiry_time : '900');
    const [userAccessTokenExpiryTime, setUserAccessTokenExpiryTime] = useState(isCredentialsAvailable ?
        additionalProperties?.user_access_token_expiry_time : '900');
    const [callbackUrlError, setCallbackUrlError] = useState('');
    const [applicationTokenExpiryError, setApplicationTokenExpiryError] = useState('');
    const [userAccessTokenExpiryError, setUserAccessTokenExpiryError] = useState('');
    const [refreshTokenExpiryError, setRefreshTokenExpiryError] = useState('');
    const [applicationTokenExpiryWarning, setApplicationTokenExpiryWarning] = useState('');
    const [userAccessTokenExpiryWarning, setUserAccessTokenExpiryWarning] = useState('');
    const [idTokenExpiryWarning, setIdTokenExpiryWarning] = useState('');
    const [idTokenExpiryError, setIdTokenExpiryError] = useState('');
    const consumerKey = isCredentialsAvailable && currentKey ? currentKey.consumerKey : '';
    const consumerSecret = isCredentialsAvailable && currentKey ? currentKey.consumerSecret : '';
    const base64Encoded = window.btoa(consumerKey + ':' + consumerSecret);
    const [selectedTab, setSelectedTab] = useState(0);
    const [managePermissionButtonEnabled, setManagePermissionButtonEnabled] = useState(false);
    const [asgardeoApplicationId, setAsgardeoApplicationId] = useState('');
    const isManagePermissionButtonDisabled = !managePermissionButtonEnabled || isLoading || isTokenGenerating;
    const { selectedOrg, userInfo,  user } = userReducer;
    const organizationHandle = selectedOrg.handle;

    const [idpList, setIdpList] = useState<IDP[]>([]);
    const [appDevSTSList, setAppDevSTSList] = useState<IDP[]>([]);
    const defaultAsgardeoKeyManagerName = DEFAULT_ASGARDEO_KEY_MANAGER_PREFIX + userReducer.selectedOrg.handle;
    const isKeyFromAsgardeoKM = isCredentialsAvailable && currentKey && (currentKey.keyManager === defaultAsgardeoKeyManagerName);
    const isOnlyAsgardeoKMAvailable = () => (idpReducer.idps?.list?.length === 1 && (idpReducer.idps?.list?.filter((idp) => idp.type === ASGARDEO_KEY_MANAGER_TYPE).length) === 1);
    const isResidentKeyManagerNotUsed = () => (!applicationKeys?.list.find((key) => key.keyManager === Settings.keyManager) || isOnlyAsgardeoKMAvailable());
    const isAsgardeoKeyManagerAvailable = () => (idpReducer.idps?.list?.filter((idp) => idp.type === ASGARDEO_KEY_MANAGER_TYPE &&
        idp.name === defaultAsgardeoKeyManagerName).length) > 0;
    const [supportedGrantTypes, setSupportedGrantTypes] = useState<any[]>(isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed() ?
        Settings.asgardeoGrantTypes : Settings.grantTypes);
    const [selectedGrantTypes, setSelectedGrantTypes] = useState<string[]>(isCredentialsAvailable && currentKey ?
        currentKey?.supportedGrantTypes : [CLIENT_CREDENTIALS_GRANT_TYPE]);
    const [disableCallbackUrl, setDisableCallbackUrl] = useState(!selectedGrantTypes?.includes(AUTHORIZATION_CODE_GRANT_TYPE)
        || !selectedGrantTypes?.includes(IMPLICIT_GRANT_TYPE));

    const [isTokenExchangeSelected, setTokenExchangeFlag] = useState(selectedGrantTypes?.
        includes(TOKEN_EXCHANGE_GRANT_TYPE));
    const [isAuthorizationCodeSelected, setIsAuthorizationCodeSelected] = useState(selectedGrantTypes?.
        includes(AUTHORIZATION_CODE_GRANT_TYPE));
    const [isClientCredentialsSelected, setIsClientCredentialsSelected] = useState(selectedGrantTypes?.
        includes(CLIENT_CREDENTIALS_GRANT_TYPE));
    const [subscriptionScopes, setSubscriptionScopes] = useState<string[]>([]);
    const [selectedScopes, setSelectedScopes] = useState<string[]>(subscriptionScopes);
    const [generatedScopesForToken, setGeneratedScopesForToken] = useState(subscriptionScopes);
    const [callbackUrls, setCallbackUrls] = useState<string[]>([]);   
    const [clientId, setClientId] = useState<string>('');
    const [idpRegistered, setIdpRegistered] = useState<boolean>(false);
    const [isClientEditing, setClientEditing] = useState<boolean>(false); 
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false); 
    const initialIDP: IDP = {
            id: '',
            name: 'Default',
            type: 'default',
            revokeEndpoint: '',
            tokenEndpoint: '',
            alias: '',
            enabled: false
    };
    const [selectedIdp, setSelectedIdp] = useState<IDP>();

    const editButtonLabel = intl.formatMessage({
        id: 'application.productionKeys.oAuth.editButton.label',
        defaultMessage: 'Edit'
    });

    const addButtonLabel = intl.formatMessage({
        id: 'application.productionKeys.oAuth.addButton.label',
        defaultMessage: 'Add'
    });

    const validateTokenExpiryTime = (expTime: string) => {
        const expiryTime = expTime.trim();
        if (expiryTime === "") {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.expiryTime.empty.helperText',
                defaultMessage: 'Expiry time cannot be empty'
            });
        } else if (Number(expiryTime) < 0) {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.expiryTime.negative.helperText',
                defaultMessage: 'Expiry time cannot be negative'
            });
        } else if (isNaN(Number(expiryTime))) {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.expiryTime.nonNumeric.helperText',
                defaultMessage: 'Expiry time can only contain numbers'
            });
        } else {
            return "";
        }
    };

    const validateTokenExpiryTimeRecommendation = (expiryTime: number) => {
        if (expiryTime > 900) {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.expiryTime.warning.helperText',
                defaultMessage: 'Recommended expiry time is 900 seconds or less'
            });
        }
        return "";
    }

    const validateCallbackUrl = (callbackUrl: string) => {
        const url = Joi.string().uri({ scheme: ['http', 'https'] });
        if ((!callbackUrls || callbackUrls?.length == 0 ) && callbackUrl === "") {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.callbackUrl.empty.helperText',
                defaultMessage: 'Redirect URL is required'
            });
        }
        const isError = url.validate(callbackUrl).error;
        if (!isError) {
            return "";
        } else {
            if (callbackUrls && callbackUrl == '') {
                return ""
            }
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.callbackUrl.invalid.helperText',
                defaultMessage: 'Enter a valid URL'
            });        
        }
    };

    const isKeyMapped = () => currentKey?.keyType === keyType && currentKey?.mode === 'MAPPED';

    const getEndpoints = (): {tokenEndpoint: string, revokeEndpoint: string, authorizeEndpoint: string} => {
        const selectedKeyManager = idpList.find((idp: IDP) => {
            return idp.name === currentKey?.keyManager;
        })
        if (isKeyMapped() || selectedKeyManager?.name === defaultAsgardeoKeyManagerName || selectedKeyManager?.type === APPDEV_STS_KEY_MANAGER_TYPE) {
            return {
                tokenEndpoint: selectedKeyManager?.tokenEndpoint || '',
                revokeEndpoint: selectedKeyManager?.revokeEndpoint || '',
                authorizeEndpoint: selectedKeyManager?.authorizeEndpoint || ''
            }
        } else {
            if (currentKey?.additionalProperties?.internalKMBaseUrl && currentKey?.additionalProperties?.internalKMBaseUrl !== '') {
                return {
                    tokenEndpoint: currentKey?.additionalProperties?.internalKMBaseUrl + RESOURCE_OAUTH2 + RESOURCE_TOKEN,
                    revokeEndpoint: currentKey?.additionalProperties?.internalKMBaseUrl + RESOURCE_OAUTH2 + RESOURCE_REVOKE,
                    authorizeEndpoint: currentKey?.additionalProperties?.internalKMBaseUrl + RESOURCE_OAUTH2 + RESOURCE_AUTHORIZE
                }
            } else {
                return {
                    tokenEndpoint: Settings.tokenEndpoint,
                    revokeEndpoint: Settings.revokeEndpoint,
                    authorizeEndpoint: Settings.tokenEndpoint.replace(RESOURCE_TOKEN, RESOURCE_AUTHORIZE)
                }
            }
        }
    }

    const isDefaultIdPAvailable = () => 
        isAsgardeoKeyManagerAvailable() || 
        idpList?.filter((idp) => idp.type === "default" && idp.enabled).length > 0 || 
        (appDevSTSList?.length ?? 0) > 0;

    useEffect(() => {
        if (!idpReducer.isLoading && idpReducer?.idps?.list) {
            setIdpList(idpReducer.idps.list);
            setAppDevSTSList(idpReducer.idps.list.filter((idp: any) => idp.type === APPDEV_STS_KEY_MANAGER_TYPE));
            setSupportedGrantTypes(isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed() ?
                Settings.asgardeoGrantTypes : Settings.grantTypes);
        }
    }, [idpReducer]);

    useEffect(() => {
        if (!isKeyUpdating && applicationId) {
            const organizationId = userReducer.selectedOrg.uuid;
            dispatchFetchAppKeys(applicationId, organizationId);
            setClientEditing(false);
        }
    }, [isKeyUpdating]);

    useEffect(()=>{
        setCurrentKey(undefined);
        setAsgardeoApplicationId('');
        setManagePermissionButtonEnabled(false);
        applicationKeys && applicationKeys.list?.map((key: Key) => {
            if (key.keyType === keyType) {
                setCurrentKey(key);
                const asgardeoAppId = key.additionalProperties?.asgardeo_app_id;
                if (asgardeoAppId) {
                    setAsgardeoApplicationId(asgardeoAppId as string);
                    setManagePermissionButtonEnabled(true);
                }
            }
        });
    }, [applicationKeys, keyType]);

    useEffect(() => {
        if (applicationId) {
            dispatchFetchApiKeys("", applicationId, selectedOrg.uuid, keyType);
        }
    }, [applicationId, keyType]);

    const onApiKeyConfirmationClose = () => {
        if (applicationId) {
            dispatchFetchApiKeys("", applicationId, selectedOrg.uuid, keyType);
        }
    }

    useEffect(() => {
        if (applicationId && keyType) {
            const organizationId = userReducer.selectedOrg.uuid;
            dispatch(createOauthTokenSuccess({ accessToken: '', tokenScopes: [], validityTime: 0 }));
            if (!Settings.devPortalIdPConfigurationFeatureEnabled || (currentKey?.keyMappingId)) {
                dispatchFetchIDPs(organizationId);
            } else {
                if (keyType === KeyType.PRODUCTION) {
                    dispatchFetchIDPs(organizationId, DevPortalAppEnv.PROD);
                } else if (keyType === KeyType.SANDBOX) {
                    dispatchFetchIDPs(organizationId, DevPortalAppEnv.SANDBOX);
                }
            }
        }
    }, [currentKey]);

    useEffect(() => {
        if (!isKeyMapping && applicationKeysReducer.isKeyMappingSuccess && applicationId) {
            const organizationId = userReducer.selectedOrg.uuid;
            dispatchFetchAppKeys(applicationId, organizationId);
        }
    }, [isKeyMappingSuccess, isKeyMapping]);

    useEffect(() => {
        setDisableCallbackUrl(!selectedGrantTypes?.includes(AUTHORIZATION_CODE_GRANT_TYPE) &&
            !selectedGrantTypes?.includes(IMPLICIT_GRANT_TYPE));
        setCallbackUrlError(validateCallbackUrl(""));
        setIsAuthorizationCodeSelected(selectedGrantTypes?.includes(AUTHORIZATION_CODE_GRANT_TYPE));
        setIsClientCredentialsSelected(selectedGrantTypes?.includes(CLIENT_CREDENTIALS_GRANT_TYPE));
    }, [selectedGrantTypes]);

    useEffect(() => {
        if (disableCallbackUrl) {
            setCallbackUrl('');
            setCallbackUrlError('');
        } else {
            setCallbackUrl('');
        }
    }, [disableCallbackUrl]);

    useEffect(() => {
        if(callbackUrl === '' && callbackUrls?.length === 0) {
            setCallbackUrlError(validateCallbackUrl(''));
        }
      }, [callbackUrls]);

    useEffect(() => {
        if (isCredentialsAvailable && currentKey && additionalProperties) {
            const appTokenExpTime = additionalProperties.application_access_token_expiry_time;
            setApplicationTokenExpiryTime(appTokenExpTime);
            setApplicationTokenExpiryWarning(validateTokenExpiryTimeRecommendation(Number(appTokenExpTime)));

            const userTokenExpTime = additionalProperties.user_access_token_expiry_time;
            setUserAccessTokenExpiryTime(userTokenExpTime);
            setUserAccessTokenExpiryWarning(validateTokenExpiryTimeRecommendation(Number(userTokenExpTime)));

            const idTokenExpTime = additionalProperties.id_token_expiry_time;
            setIdTokenExpiryTime(idTokenExpTime);
            setIdTokenExpiryWarning(validateTokenExpiryTimeRecommendation(Number(idTokenExpTime)));

            setRefreshTokenExpiryTime(additionalProperties.refresh_token_expiry_time);
            setIsPublicClientEnabled(additionalProperties.bypassClientCredentials as boolean);
            setCallbackUrl('');
            setCallbackUrlError('');
            if (currentKey.callbackUrl) {
                setCallbackUrls(currentKey.callbackUrl.split(','));
            }
        }
        if (isCredentialsAvailable && currentKey && currentKey.supportedGrantTypes) {
            setSelectedGrantTypes([...currentKey.supportedGrantTypes])
            setIsGenerateTokenDisabled(!currentKey.supportedGrantTypes.includes(CLIENT_CREDENTIALS_GRANT_TYPE))
        }
        if (!isCredentialsAvailable) {
            // Set defaults for token expiration time when credentials are not available
            setApplicationTokenExpiryTime('900');
            setUserAccessTokenExpiryTime('900');
            setIdTokenExpiryTime('900');
        }
        setSupportedGrantTypes(isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed() ?
            Settings.asgardeoGrantTypes : Settings.grantTypes);
    }, [isCredentialsAvailable, additionalProperties, currentKey]);

    useEffect(() => {
        if (currentKey) {
            setIdpRegistered(true);
            const idp = idpList?.find((idp: any) => {
                if (idp.name === currentKey.keyManager) {
                    return idp;
                }
            });
            setClientId(currentKey.consumerKey);
            setSelectedIdp(idp);
        } else {
            setIdpRegistered(false);
            if (Settings.devPortalIdPConfigurationFeatureEnabled && !isDefaultIdPAvailable() && idpList?.length > 0) {
                setSelectedIdp(idpList[0]);
            } else {
                setSelectedIdp(initialIDP);
            }
            setClientId('');
        }
    }, [currentKey, idpList]);

    useEffect(() => {
        const scopesAllocatedInApp = application?.subscriptionScopes ?
            application?.subscriptionScopes.map((scope) => { return scope.key }) : []
        setSubscriptionScopes(scopesAllocatedInApp);
        setSelectedScopes(scopesAllocatedInApp);
        setGeneratedScopesForToken(scopesAllocatedInApp);
    }, [application]);

    const generateAccessToken = async (selectedPermissions: string[]) => {
        try {
            if (currentKey) {
                const response = await generateToken(
                    selectedPermissions,
                    applicationId as string,
                    currentKey,
                );
                setToken(response?.data.accessToken);
                setGeneratedScopesForToken(response?.data.tokenScopes);
                setIsTokenGenerating(false);
                setTokenOpen(true);
            }
        } catch (error) {
            const errorMessage = intl.formatMessage({
                id: 'application.oAuth.tokenGeneration.error',
                defaultMessage: 'Error occurred while generating token for the application'
            });
            dispatch(setErrorNotification(errorMessage));
        }
    };

    const fetchAccessTokenForApp = async () => {
        await generateAccessToken(selectedScopes);
    };

    const onApplicationTokenExpiryChange = (event: ChangeEvent<HTMLInputElement>) => {
        const expiryTime = event.target.value;
        setApplicationTokenExpiryTime(expiryTime);
        const validationError = validateTokenExpiryTime(expiryTime);
        setApplicationTokenExpiryError(validationError);
        if (!validationError) {
            const validationWarning = validateTokenExpiryTimeRecommendation(Number(expiryTime));
            setApplicationTokenExpiryWarning(validationWarning);
        }
    };

    const onUserAccessTokenExpiryChange = (event: ChangeEvent<HTMLInputElement>) => {
        const expiryTime = event.target.value;
        const validationError = validateTokenExpiryTime(expiryTime);
        setUserAccessTokenExpiryTime(expiryTime);
        setUserAccessTokenExpiryError(validationError);
        if (!validationError) {
            const validationWarning = validateTokenExpiryTimeRecommendation(Number(expiryTime));
            setUserAccessTokenExpiryWarning(validationWarning);
        }
    };

    const onRefreshTokenExpiryChange = (event: ChangeEvent<HTMLInputElement>) => {
        const expiryTime = event.target.value;
        setRefreshTokenExpiryError(validateTokenExpiryTime(expiryTime));
        setRefreshTokenExpiryTime(expiryTime);
    };

    const onIdTokenExpiryChange = (event: ChangeEvent<HTMLInputElement>) => {
        const expiryTime = event.target.value;
        setIdTokenExpiryTime(expiryTime);
        const validationError = validateTokenExpiryTime(expiryTime);
        setIdTokenExpiryError(validationError);
        if (!validationError) {
            const validationWarning = validateTokenExpiryTimeRecommendation(Number(expiryTime));
            setIdTokenExpiryWarning(validationWarning);
        }
    };

    const handleTokenExchangeTypeSelection = () => {
        if (selectedGrantTypes.includes(TOKEN_EXCHANGE_GRANT_TYPE)) {
            setTokenExchangeFlag(true)
        } else {
            setTokenExchangeFlag(false)
        }
    }

    const handleGenerateCredentialsClick = () => {
        const organizationId = userReducer.selectedOrg.uuid;
        const additionalProperties = {
            'application_access_token_expiry_time': applicationTokenExpiryTime,
            'refresh_token_expiry_time': refreshTokenExpiryTime,
            'id_token_expiry_time': idTokenExpiryTime,
            'user_access_token_expiry_time': userAccessTokenExpiryTime,
            'bypassClientCredentials': isPublicClientEnabled? "true" : "false",
            'pkceMandatory': isPkceEnabled ? "true" : "false",
        };
        trackAppProdKeyGeneration(
            `${applicationId}`,
            `${applicationTokenExpiryTime}`,
            `${refreshTokenExpiryTime}`,
            `${idTokenExpiryTime}`
        );
        const callbackUrlList = callbackUrls?.toString();
        let keyManager = Settings.keyManager;
        if (isResidentKeyManagerNotUsed() && isAsgardeoKeyManagerAvailable()) {
            keyManager = defaultAsgardeoKeyManagerName;
        } else if (appDevSTSList?.length > 0) {
            keyManager = appDevSTSList.find((idp: any) => idp.name.includes('_us_prod'))?.name || 
                appDevSTSList.find((idp: any) => idp.name.includes('_prod'))?.name || appDevSTSList[0].name;
        }
        dispatchGenerateAppKey(applicationId as string, organizationId, selectedGrantTypes, callbackUrlList,
            additionalProperties, keyType, keyManager);
        handleTokenExchangeTypeSelection();
    };

    const handleUpdateClick = () => {
        if (currentKey) {
            const additionalProperties = {
                'application_access_token_expiry_time': applicationTokenExpiryTime,
                'refresh_token_expiry_time': refreshTokenExpiryTime,
                'id_token_expiry_time': idTokenExpiryTime,
                'user_access_token_expiry_time': userAccessTokenExpiryTime,
                'bypassClientCredentials': isPublicClientEnabled ? "true" : "false",
                'pkceMandatory': isPkceEnabled ? "true" : "false",
            };
            const callbackUrlList = callbackUrls?.toString();
            const updatedKey = {
                ...currentKey,
                callbackUrl: callbackUrlList,
                supportedGrantTypes: selectedGrantTypes,
                additionalProperties: additionalProperties
            };
            dispatchUpdateAppKey(applicationId as string, updatedKey);
            handleTokenExchangeTypeSelection();
        } else {
            const errorMessage = intl.formatMessage({
                id: 'application.productionKeys.oAuth.update.keyNotFound.error',
                defaultMessage: 'No key found for the environment type: ' + keyType
            });
            dispatch(setErrorNotification(errorMessage));
        }
    };

    const handleSelectedGrantTypes = (event: ChangeEvent<any>) => {
        const {checked, value} = event.target;
        if (checked) {
            setSelectedGrantTypes([...selectedGrantTypes, value]);
            if (value === TOKEN_EXCHANGE_GRANT_TYPE) {
                setTokenExchangeFlag(true)
            }
        } else {
            setSelectedGrantTypes(selectedGrantTypes.filter((item) => {
                if (value === TOKEN_EXCHANGE_GRANT_TYPE) {
                    return !(item === REFRESH_TOKEN_GRANT_TYPE || item === TOKEN_EXCHANGE_GRANT_TYPE);
                } else {
                    return value !== item;
                }
            }));
            if (value === TOKEN_EXCHANGE_GRANT_TYPE) {
                setTokenExchangeFlag(false);
                setIsPublicClientEnabled(false);
            }
            if (value === AUTHORIZATION_CODE_GRANT_TYPE) {
                setIsPkceEnabled(false);
            }
        }
    };

    const onChangeCallbackUrl = (event: ChangeEvent<HTMLInputElement>) => {
        const callbackUrl = event.target.value;
        setCallbackUrlError(validateCallbackUrl(callbackUrl));
        setCallbackUrl(callbackUrl);
    };

    const handleClickCurlOpen = () => {
        setCurlOpen(true);
    };

    const handleClickCurlClose = () => {
        setCurlOpen(false);
    };

    const handleClickTokenOpen = () => {
        fetchAccessTokenForApp();
        setTokenOpen(true);
    };

    const setPermissionsCallback = (permissions: string[]) => {
        setSelectedScopes(permissions);
    }

    const handleClickGenerate = () => {
        if (selectedScopes.length !== 0) {
            setSubscriptionScopes(selectedScopes);
        }
        setToken('');
        setIsTokenGenerating(true);
        generateAccessToken(selectedScopes);
        trackAppTestKeyGeneration(`${applicationId}`);
    }

    const handleClickTokenClose = () => {
        setTokenOpen(false);
        setToken('');
        setSelectedScopes(subscriptionScopes);
        setIsTokenGenerating(false);
        dispatch(createOauthTokenSuccess({ accessToken: '', tokenScopes: [], validityTime: 0 }));
    };

    const handleTabChange = (event: React.ChangeEvent<unknown>, newValue: number) => {

        setSelectedTab(newValue);
    };

    const handlePublicClientChange = () => {
        if (!isPublicClientEnabled) {
            setSelectedGrantTypes(selectedGrantTypes.filter(item => item !== CLIENT_CREDENTIALS_GRANT_TYPE));
        }
        setIsPublicClientEnabled(!isPublicClientEnabled);
    }

    const handleEnablePkceChange = () => {
        setIsPkceEnabled(!isPkceEnabled);
    };

    const handleClientIdChange = (clientId: string) => {
        setClientId(clientId);
    };

    const isExternalIDPsConfigured = () => (idpList.filter((idp) => idp.type !== "default" && idp.type !== APPDEV_STS_KEY_MANAGER_TYPE &&
                                            idp.enabled).length) > 0

    const publicClientDescription = (
        <FormattedMessage
            id='application.productionKeys.oAuth.publicClient.main.description'
            defaultMessage='Allow authentication without the client secret'
        />
    );

    const enablePkceDescription = (
        <FormattedMessage
            id='components.app.appProductionKeys.oAuth.enablePkce.title'
            defaultMessage='Mandatory'
        />
    );

    const getTokenGenerateBtnText = () => {
        return intl.formatMessage({
            id: 'components.app.appProductionKeys.oAuth.tokenGeneration.buttonText',
            defaultMessage: 'Generate Token'
        });
    };

    const getTokenGenerateCurlBtnText = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oauth.tokenGeneration.curl.buttonText',
            defaultMessage: 'cURL'
        });
    };

    const getCallbackUrlHelperText = () => {
        if (callbackUrl) {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.callbackUrl.action.helperText',
                defaultMessage: "Press enter to add"
            });
        }
    };

    const getApplicationTokenHelperText = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.applicationAccessTokenExpiryTime.helperText',
            defaultMessage: 'Type application access token expiry time in seconds'
        });
    };

    const userTokenHelperText = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.userAccessTokenExpiryTime.helperText',
            defaultMessage: 'Type user access token expiry time in seconds'
        });
    };

    const getRefreshTokenHelperText = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.refreshTokenExpiryTime.helperText',
            defaultMessage: 'Type refresh token expiry time in seconds'
        });
    };

    const getIdTokenHelperText = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.idTokenExpiryTime.helperText',
            defaultMessage: 'Type ID token expiry time in seconds'
        });
    };

    const getUserKeysTooltip = () => {
        if (!isCredentialsAvailable) {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.keys.userKeys',
                defaultMessage: 'Generate Application Keys'
            });
        } else if (isCredentialsAvailable && !isTokenExchangeSelected) {
            return intl.formatMessage({
                id: 'application.productionKeys.oAuth.tokenExchange.notSelected',
                defaultMessage: 'Update the Application Keys by enabling token exchange'
            });
        } else {
            return ""
        }
    }

    const getPublicClientDisabledTooltip = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.publicClient.disabled.tooltip',
            defaultMessage: 'Please disable the client credentials grant type to allow authentication without the client secret'
        });
    }

    const getRefreshTokenDisabledTooltip = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.refreshToken.tooltip',
            defaultMessage: 'Please enable the token exchange grant type to enable refresh token grant type'
        });
    }

    const getExternalIdpAddedTooltip = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.tokenExchange.tooltip',
            defaultMessage: 'Please add an external identity provider to use this grant type'
        });
    }

    const getClientCredentialsDisabledTooltip = () => {
        return intl.formatMessage({
            id: 'application.productionKeys.oAuth.clientCredentials.tooltip',
            defaultMessage: 'Please disallow authentication without the client secret to use client credentials grant type'
        });
    }

    const confirmationDialogI18n = {
        title: intl.formatMessage({
            id: 'application.productionKeys.oAuth.clientCredentials.editConfirmationDialog.title',
            defaultMessage: 'Update Client ID'
        }),
        content: intl.formatMessage({
            id: 'application.productionKeys.oAuth.clientCredentials.editConfirmationDialog.content',
            defaultMessage: 'Editing the client ID may impact existing client requests. Are you sure you want to proceed?'
        }),
        okayButtonLabel: intl.formatMessage({
            id: 'application.productionKeys.oAuth.clientCredentials.editConfirmationDialog.okButtonLabel',
            defaultMessage: 'Update'
        }),
        cancelButtonLabel: intl.formatMessage({
            id: 'application.productionKeys.oAuth.clientCredentials.editConfirmationDialog.cancelButtonLabel',
            defaultMessage: 'Cancel'
        }),
        loadingText: intl.formatMessage({
            id: 'application.productionKeys.oAuth.clientCredentials.editConfirmationDialog.loadingText',
            defaultMessage: 'Saving...'
        })
    };

    const buttonDisabled = isLoading || applicationTokenExpiryError !== "" || refreshTokenExpiryError !== ""
        || idTokenExpiryError !== "" || !disableCallbackUrl && callbackUrlError !== "" || userAccessTokenExpiryError !== ""
        || !disableCallbackUrl && callbackUrls && callbackUrls.length == 0 || selectedGrantTypes?.length == 0 
        || (selectedGrantTypes?.length == 1 && selectedGrantTypes?.includes(REFRESH_TOKEN_GRANT_TYPE));

    const NoKeysHelpText = () => {
        return (
            <Grid container>
                <Grid item>
                    <Typography variant='body1' className={classes.noKeyDescription}>
                        <FormattedMessage
                            id='application.productionKeys.oAuth.Keys.notGenerated'
                            defaultMessage='No client ID and client secret to display.'
                        />
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    const GenerateKeysButton = () => {
        return (
            <Box
                className={clx({
                    [classes.genKeysButtonBox]:
                    isCredentialsAvailable,
                    [classes.genKeysButtonBoxNoKeys]:
                        !isCredentialsAvailable,
                })}
            >
                <Box>
                    <Button
                        disabled={buttonDisabled}
                        variant="contained"
                        color="primary"
                        classes={{ root: classes.genKeysButton }}
                        onClick={handleGenerateCredentialsClick}
                        data-testid='generate-oauth-key'
                    >
                        <FormattedMessage
                            id='application.productionKeys.oAuth.generateKeyButton.label'
                            defaultMessage='Generate Credentials'
                        />
                    </Button>
                </Box>
            </Box>
        );
    }

    const getGrantTypeDisableTooltips = (grantType: GrantType) => {
        return (
            (
                !(isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed()) &&
                isExternalIDPsConfigured() && !isTokenExchangeSelected &&
                grantType.value === REFRESH_TOKEN_GRANT_TYPE &&
                getRefreshTokenDisabledTooltip()
            ) ||
            !(isExternalIDPsConfigured()) && (
                grantType.value === REFRESH_TOKEN_GRANT_TYPE ||
                grantType.value === TOKEN_EXCHANGE_GRANT_TYPE
            ) && getExternalIdpAddedTooltip() ||
            (
                isPublicClientEnabled &&
                grantType.value === CLIENT_CREDENTIALS_GRANT_TYPE &&
                getClientCredentialsDisabledTooltip()
            ) || ''
        )
    }

    const handleDelete = (url: string) => {
        const existinUrls = [... callbackUrls]
        existinUrls.splice(existinUrls.indexOf(url), 1)
        setCallbackUrls(existinUrls);
    };

    const isGrantTypeDisabled = (grantType: GrantType) => {
        return (
            (
                isPublicClientEnabled &&
                grantType.value === CLIENT_CREDENTIALS_GRANT_TYPE
            ) || 
            (
                !(isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed()) &&
                !(isExternalIDPsConfigured()) &&
                (
                    grantType.value === REFRESH_TOKEN_GRANT_TYPE ||
                    grantType.value === TOKEN_EXCHANGE_GRANT_TYPE
                )
            ) ||
            (
                !(isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed()) &&
                isCredentialsAvailable &&
                isExternalIDPsConfigured() &&
                !isTokenExchangeSelected &&
                grantType.value === REFRESH_TOKEN_GRANT_TYPE
            ) || 
            ( Settings.devPortalIdPConfigurationFeatureEnabled && 
                selectedGrantTypes?.length === 1 && 
                selectedGrantTypes?.includes(CLIENT_CREDENTIALS_GRANT_TYPE) && 
                grantType.value === REFRESH_TOKEN_GRANT_TYPE
            )                           
        )
    }

    const onPressEnter = (event: KeyboardEvent) => {
        if(event.key === 'Enter' && callbackUrlError == ""){
            if (callbackUrls?.includes(callbackUrl)) {
                setCallbackUrlError(intl.formatMessage({
                    id: 'application.productionKeys.oAuth.callbackUrl.duplicate.helperText',
                    defaultMessage: 'This redirect URL is already added.'
                }));
            } else {
                callbackUrls.push(callbackUrl);
                setCallbackUrl('');
            }
           
        }
    };

    const setIdp = (selection: any) => {
        const idp = idpList.find((idp) => idp.name === selection.target.value);
        if (idp) {
            setSelectedIdp(idp);
        } else {
            setSelectedIdp(initialIDP);
        }
    };

    const handleClientAdd = () => {
        const payload: ApplicationKeyMapping = {
            consumerKey: clientId,
            keyType: keyType,
            keyManager: selectedIdp?.name || '',
        }
        if (applicationId && clientId && selectedIdp) {
            dispatchSetApplicationKeyMap(applicationId, payload);
        }
    };

    const confirmUpdate = () => {
        if (currentKey) {
            const updatedKey = {
                ...currentKey,
                consumerKey: clientId,
                supportedGrantTypes: selectedGrantTypes,
                additionalProperties: additionalProperties
            };
            dispatchUpdateAppKey(applicationId as string, updatedKey);
        }
        setIsDialogOpen(false);
    };

    const cancelUpdate = () => {
        setIsDialogOpen(false);
        setClientEditing(false);
    };

    const handleClientEdit = () => {
        setIsDialogOpen(true);
        setClientEditing(true);
    }

    const isUserGrantEnabled = (): boolean => {
        return selectedGrantTypes?.length !== 0 && !(selectedGrantTypes?.length === 1 && selectedGrantTypes?.includes(CLIENT_CREDENTIALS_GRANT_TYPE)); 
    }

    const availableExternalIdps = (): any[] => 
        idpList.filter((idp: any) => idp.tokenType === IdpRegisterType.EXTERNAL && idp.enabled);

    const IdpList = () => {
        return (
            <Grid container>
                {!(Settings.devPortalIdPConfigurationFeatureEnabled && idpList?.length === 1 && selectedIdp) && (
                    <>
                        <Grid item md={12}  className={classes.idpGrid}>
                            <Typography variant="subtitle1" gutterBottom>
                                <FormattedMessage
                                    id='application.productionKeys.oAuth.identityProvider'
                                    defaultMessage='Identity Provider' />
                            </Typography>
                        </Grid>
                        <Grid item md={2} className={classes.idpGrid}>
                            <FormGroup row>
                                <FormControl
                                    variant="outlined"
                                    className={classes.idpSelection}
                                >
                                    <Select
                                        value={selectedIdp?.name}
                                        key={selectedIdp?.name}
                                        onChange={setIdp}
                                        variant="outlined"
                                        className={classes.selectBox}
                                        MenuProps={{
                                            getContentAnchorEl: null,
                                            classes: { paper: classes.dropdownStyle },
                                            anchorOrigin: {
                                                vertical: "bottom",
                                                horizontal: "left",
                                            }
                                        }}
                                        data-testid='idp-select'
                                    >
                                        <MenuItem value="" disabled>
                                            <em>
                                                <FormattedMessage
                                                    id="application.productionKeys.oAuth.externalIdp.select"
                                                    defaultMessage="Select an Identity Provider"
                                                />
                                            </em>
                                        </MenuItem>
                                        <MenuItem key={initialIDP.name} value={initialIDP.name}>
                                            <FormattedMessage
                                                id="application.productionKeys.oAuth.externalIdp.default"
                                                defaultMessage="Default"
                                            />
                                        </MenuItem>
                                        {availableExternalIdps().map((idp: IDP) => (
                                            <MenuItem key={idp.name} value={idp.name}>
                                                <FormattedMessage
                                                    id="application.productionKeys.oAuth.externalIdp.name"
                                                    defaultMessage="{name} ( {type} )"
                                                    values={{ type: idp.type, name: idp.name }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </FormGroup>
                        </Grid>
                    </>
                )}
            </Grid>
        )
    }

    const ClientEditActions = () => {
      return (
        <Box display="flex" data-testid="client-id-edit-actions">
          {!isClientEditing && (
            <OutlineButton
              onClick={handleClientEdit}
              className={classNames(classes.outlineBtn, classes.editBtn)}
              buttonLabel={editButtonLabel}
              testId="edit-client"
            />
          )}
        </Box>
      );
    };

    const TokenEndpointList = () => {
        return (
            <>
                <TableRow data-testid="endpoint-list">
                    <TableCell colSpan={2} className={classes.tableCellBorder}>
                        <Typography variant="subtitle1" className={classes.subTitle}>
                            <FormattedMessage
                                id='application.productionKeys.oAuth.endpoints'
                                defaultMessage='Endpoints'
                            />
                        </Typography>
                    </TableCell>
                </TableRow>
                    <TableRow data-testid="token-endpoint">
                        <TableCell className={classes.tableCellBorder} align='left'>
                            <Typography className={classes.keyLabel}>
                                <FormattedMessage
                                    id='application.productionKeys.oAuth.tokenEndpoint'
                                    defaultMessage='Token Endpoint'
                                />
                            </Typography>
                        </TableCell>
                        <TableCell className={classes.td}>
                            <CopyTextField
                                className={classes.tdValueCell}
                                textContent={getEndpoints().tokenEndpoint}
                                disabled={true}
                                testId="token-endpoint-value"
                            />
                        </TableCell>
                    </TableRow>
                    { getEndpoints().revokeEndpoint && 
                        <TableRow data-testid="revoke-endpoint">
                            <TableCell className={classes.tableCellBorder} align='left'>
                                <Typography className={classes.keyLabel}>
                                    <FormattedMessage
                                        id='application.productionKeys.oAuth.revokeEndpoint'
                                        defaultMessage='Revoke Endpoint'
                                    />
                                </Typography>
                            </TableCell>
                            <TableCell className={classes.td}>
                                <CopyTextField
                                    className={classes.tdValueCell}
                                    textContent={getEndpoints().revokeEndpoint}
                                    disabled={true}
                                    testId="revoke-endpoint-value"
                                />
                            </TableCell>
                        </TableRow>   
                    }
                    {(isKeyMapped() || (isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed()))&&
                        <TableRow data-testid="authorize-endpoint">
                            <TableCell className={classes.tableCellBorder} align='left'>
                                <Typography className={classes.keyLabel}>
                                    <FormattedMessage
                                        id='components.app.appProductionKeys.oAuth.index.authorizeEndpoint'
                                        defaultMessage='Authorize Endpoint'
                                    />
                                </Typography>
                            </TableCell>
                            <TableCell className={classes.td}>
                                <CopyTextField
                                    className={classes.tdValueCell}
                                    textContent={getEndpoints().authorizeEndpoint}
                                    disabled={true}
                                    testId="authorize-endpoint-value"
                                />
                            </TableCell>
                        </TableRow>
                    }        
            </ >
        )
    };

    const AddIdpClientForm = () => {
      return (
        <Grid container>
          <Grid item xl={12} lg={12} md={5} sm={6} xs={12}>
            <Box className={classes.idpFormWrapper}>
              <Box>
                <Typography className={classes.keyLabel} gutterBottom>
                  <FormattedMessage
                    id="application.productionKeys.oAuth.clientId.label"
                    defaultMessage="Client ID"
                  />
                </Typography>
              </Box>

              <Box display="flex" flexDirection="row">
                <TextField
                  data-testid="client-id-input"
                  key="clientId"
                  variant="outlined"
                placeholder={
                    intl.formatMessage({
                        id: 'application.productionKeys.oAuth.enterClientId.placeholder',
                        defaultMessage: `Enter {selectedIdpName} client ID here..`
                    }, { selectedIdpName: selectedIdp?.name })
                }
                  className={classes.clientIdInput}
                  value={clientId}
                  disabled={idpRegistered && !isClientEditing}
                  onChange={(e) => setClientId(e.target.value)}
                />
                {!idpRegistered && (
                  <OutlineButton
                    onClick={handleClientAdd}
                    className={classNames(
                      classes.outlineBtn,
                      classes.addIdpButton
                    )}
                    buttonLabel={addButtonLabel}
                    data-testid="add-client"
                  />
                )}
                {idpRegistered && <ClientEditActions />}
              </Box>
            </Box>
            {!idpRegistered && (
              <Box>
                <ShowClientIDDocumentBanner idpType={selectedIdp?.type} />
              </Box>
            )}
            {isDialogOpen && (
              <ClientIdConfirmationDialog
                open={isDialogOpen}
                clientId={clientId}
                selectedIdp={selectedIdp}
                idpRegistered={idpRegistered}
                defaultAsgardeoKeyManagerName={defaultAsgardeoKeyManagerName}
                dialogTitle={confirmationDialogI18n.title}
                okayButtonLabel={confirmationDialogI18n.okayButtonLabel}
                cancelButtonLabel={confirmationDialogI18n.cancelButtonLabel}
                onClose={cancelUpdate}
                onCancel={cancelUpdate}
                onOkay={confirmUpdate}
                onChange={handleClientIdChange}
              />
            )}
          </Grid>
        </Grid>
      );
    };

    const AdvancedConfigContent = () => {
        return (
            <React.Fragment>
                <Grid container className={classes.advancedConfigGrid} direction="column"
                      justifyContent='flex-start' alignItems='flex-start'>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        className={classes.advancedConfigRaw}
                    >
                        <Grid item md={4}
                            className={clx({
                                [classes.tableCellBorderGrantTypes]:
                                    isCredentialsAvailable,
                                [classes.tableCellBorderNoKeysGrantTypes]:
                                    !isCredentialsAvailable,
                                [classes.displayInline]: true
                            })}
                        >
                            <Typography variant="body1">
                                <FormattedMessage
                                    id='application.productionKeys.oAuth.grantTypes'
                                    defaultMessage='Grant Types'
                                />
                            </Typography>
                            <InfoTooltip
                                content={intl.formatMessage({
                                    id: 'application.productionKeys.oAuth.grantTypes.tooltip',
                                    defaultMessage: 'The application can use the above grant'
                                        + ' types to generate access tokens. Based on the application'
                                        + ' requirement, you can enable or disable grant types for this'
                                        + ' application.'
                                })}
                                darken={true}
                            >
                                <span className={classes.displayInline}>
                                    <CustomIcons icon="badge-what" />
                                </span>
                            </InfoTooltip>
                        </Grid>
                        <Grid item md={8}
                              className={clx({
                                  [classes.td2]:
                                  isCredentialsAvailable,
                                  [classes.td2NoKeys]:
                                      !isCredentialsAvailable,
                              })}
                        >
                            <Box>
                                {supportedGrantTypes.map((grantType: GrantType) => (
                                    <FormControl component="fieldset" key={grantType.id}>
                                        <Tooltip title={
                                            getGrantTypeDisableTooltips(grantType)}
                                            interactive={true}>
                                            <FormGroup aria-label="position" row={true}>
                                                <FormControlLabel
                                                    key={Math.random()}
                                                    value={grantType.value}
                                                    control={
                                                        <CustomCheckbox
                                                            defaultChecked={selectedGrantTypes?.includes(grantType.value)}
                                                            disabled={isGrantTypeDisabled(grantType)}
                                                            data-testid={'chkbox-' + grantType.type}
                                                        />
                                                    }
                                                    label={grantType.type}
                                                    labelPlacement="end"
                                                    onChange={handleSelectedGrantTypes}
                                                />
                                            </FormGroup>
                                        </Tooltip>
                                    </FormControl>
                                ))}
                                {selectedGrantTypes && selectedGrantTypes.length === 0 && (
                                    <Box display="flex" flexDirection="row">
                                        <Box className={classes.warningIcon}>
                                            <CustomIcons icon={'warning'} />
                                        </Box>
                                        <Typography variant="body2" className={classes.helperTextError}>
                                            <FormattedMessage
                                                id='application.productionKeys.oAuth.grantTypes.empty.helperText'
                                                defaultMessage='Select at least one grant type'
                                            />
                                        </Typography>
                                    </Box>
                                )}
                                {selectedGrantTypes && selectedGrantTypes.length == 1 && selectedGrantTypes.includes(REFRESH_TOKEN_GRANT_TYPE) && (
                                    <Box display="flex" flexDirection="row">
                                        <Box className={classes.warningIcon}>
                                            <CustomIcons icon={'warning'} />
                                        </Box>
                                        <Typography variant="body2" className={classes.helperTextError}>
                                            <FormattedMessage
                                                id='application.productionKeys.oAuth.grantTypes.refreshToken.helperText'
                                                defaultMessage='Refresh token grant type can be used with code or password grant types'
                                            />
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                    {!disableCallbackUrl && (
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            className={classes.advancedConfigRaw}
                        >
                            <React.Fragment>
                                <Grid item md={4}
                                    className={clx({
                                        [classes.tableCellBorder]:
                                            isCredentialsAvailable,
                                        [classes.tableCellBorderNoKeys]:
                                            !isCredentialsAvailable,
                                        [classes.displayInline]: true
                                    })}
                                >
                                    <Typography variant="body1">
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.callbackUrl'
                                            defaultMessage='Redirection URLs'
                                        />
                                    </Typography>
                                    <InfoTooltip
                                        content={intl.formatMessage({
                                            id: 'application.productionKeys.oAuth.callbackUrls.tooltip',
                                            defaultMessage: "A redirection URL is a specific URL in the client application which is used by the"
                                                + " authorization server to send the client's user-agent (usually web browser) back after"
                                                + " granting access"
                                        })}
                                        darken={true}
                                    >
                                        <span className={classes.displayInline}>
                                            <CustomIcons icon="badge-what" />
                                        </span>
                                    </InfoTooltip>
                                </Grid>
                                <Grid item md={8}
                                      className={clx({
                                          [classes.td2]:
                                          isCredentialsAvailable,
                                          [classes.td2NoKeys]:
                                              !isCredentialsAvailable,
                                      })}>
                                    <TextField
                                        onChange={onChangeCallbackUrl}
                                        onKeyPress={onPressEnter}
                                        size="small"
                                        value={callbackUrl}
                                        variant="outlined"
                                        error={callbackUrlError !== ""}
                                        placeholder={'http://url-to-webapp'}
                                        FormHelperTextProps={{
                                            className: classes.helperText
                                        }}
                                        helperText={callbackUrlError === "" ?
                                            getCallbackUrlHelperText() : (
                                                <div className={classes.helperTextError}>
                                                    <div className={classes.warningIcon}>
                                                        <CustomIcons icon={'warning'}/>
                                                    </div>
                                                    <p> {callbackUrlError} </p>
                                                </div>
                                            )}
                                        className={classes.textField}
                                        data-testid='callback-url'
                                    />
                                    {callbackUrls?.map((url: string) => {
                                        return (
                                            <Chip 
                                                className={classes.urlChip} 
                                                key={Math.random()} 
                                                label={url} 
                                                onDelete={() => handleDelete(url)} />
                                        )
                                    })}
                                </Grid>
                            </React.Fragment>
                        </Grid>
                    )}
                    { !Settings.devPortalIdPConfigurationFeatureEnabled || (supportedGrantTypes?.length === 1 && supportedGrantTypes?.includes(CLIENT_CREDENTIALS_GRANT_TYPE)) && (
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            className={classes.advancedConfigRaw}
                        >
                            <Grid item md={4}
                                className={clx({
                                    [classes.tableCellBorderGrantTypes]:
                                        isCredentialsAvailable,
                                    [classes.tableCellBorderNoKeysGrantTypes]:
                                        !isCredentialsAvailable,
                                    [classes.displayInline]: true
                                })}
                            >
                                <Label
                                    label={intl.formatMessage({
                                        id: 'application.productionKeys.oAuth.publicClient',
                                        defaultMessage: 'Public Client'
                                    })}
                                    optional
                                />
                                <InfoTooltip
                                    content={intl.formatMessage({
                                        id: 'application.productionKeys.oAuth.publicClient.tooltip',
                                        defaultMessage: 'Public clients, such as applications running in a '
                                            + 'browser or on a mobile device, are unable to utilize registered client secrets.'
                                    })}
                                    darken={true}
                                >
                                    <span className={classes.displayInline}>
                                        <CustomIcons icon="badge-what" />
                                    </span>
                                </InfoTooltip>
                            </Grid>
                            <Grid item md={8}
                                className={clx({
                                    [classes.td2]:
                                    isCredentialsAvailable,
                                    [classes.td2NoKeys]:
                                        !isCredentialsAvailable,
                                })}
                            >
                                <Box>
                                    <Tooltip title={isClientCredentialsSelected && getPublicClientDisabledTooltip() || ''} interactive={true}>
                                        <FormControlLabel
                                            key={Math.random()}
                                            control={
                                                <CustomCheckbox checked={isPublicClientEnabled} disabled={isClientCredentialsSelected}/>
                                            }
                                            label={publicClientDescription}
                                            labelPlacement="end"
                                            onChange={handlePublicClientChange}
                                        />
                                    </Tooltip>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                    {supportedGrantTypes.map((grantType: GrantType) => grantType.value).includes(AUTHORIZATION_CODE_GRANT_TYPE) && (
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            className={classes.advancedConfigRaw}
                        >
                            <Grid item md={4}
                                className={clx({
                                    [classes.tableCellBorderGrantTypes]:
                                        isCredentialsAvailable,
                                    [classes.tableCellBorderNoKeysGrantTypes]:
                                        !isCredentialsAvailable,
                                    [classes.displayInline]: true
                                })}
                            >
                                <Label
                                    label={intl.formatMessage({
                                        id: 'components.app.AppProductionKeys.Oauth.index.enablePKC',
                                        defaultMessage: 'PKCE for enhanced security'
                                    })}
                                    className={classes.flexTextField}
                                    optionalFlagClassName={classes.multipleLineOptionalFlag}
                                    optional
                                />
                                <InfoTooltip
                                    content={intl.formatMessage({
                                        id: 'application.productionKeys.oAuth.pkce.tooltip',
                                        defaultMessage: 'Applications must include a Code Challenge in the Authorization Code Request'
                                    })}
                                    darken={true}
                                >
                                    <span className={classes.displayInline}>
                                        <CustomIcons icon="badge-what" />
                                    </span>
                                </InfoTooltip>
                            </Grid>
                            <Grid item md={8}
                                className={clx({
                                    [classes.td2]:
                                        isCredentialsAvailable,
                                    [classes.td2NoKeys]:
                                        !isCredentialsAvailable,
                                })}
                            >
                                <Box>
                                    <FormControlLabel
                                        key={Math.random()}
                                        control={
                                            <CustomCheckbox checked={isPkceEnabled} disabled={!isAuthorizationCodeSelected}/>
                                        }
                                        label={enablePkceDescription}
                                        labelPlacement="end"
                                        onChange={handleEnablePkceChange}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        className={classes.advancedConfigRaw}
                    >
                        <Grid item md={4}
                              className={clx({
                                  [classes.tableCellBorder]:
                                  isCredentialsAvailable,
                                  [classes.tableCellBorderNoKeys]:
                                      !isCredentialsAvailable,
                              })}
                        >
                            <Typography
                                variant="body1"
                            >
                                <FormattedMessage
                                    id='application.productionKeys.oAuth.applicationAccessToken.expiryTime'
                                    defaultMessage='Application access token expiry time'
                                />
                            </Typography>
                        </Grid>
                        <Grid item md={8}
                              className={clx({
                                  [classes.td2]:
                                  isCredentialsAvailable,
                                  [classes.td2NoKeys]:
                                      !isCredentialsAvailable,
                              })}
                        >
                            <TextField
                                onChange={onApplicationTokenExpiryChange}
                                size="small"
                                value={applicationTokenExpiryTime}
                                variant="outlined"
                                error={applicationTokenExpiryError !== ""}
                                FormHelperTextProps={{
                                    className: classes.helperText
                                }}
                                helperText={applicationTokenExpiryError !== "" ?
                                    (
                                        <div className={classes.helperTextError}>
                                            <div className={classes.warningIcon}>
                                                <CustomIcons icon={'warning'} />
                                            </div>
                                            <p> {applicationTokenExpiryError} </p>
                                        </div>
                                    ) : applicationTokenExpiryWarning !== "" ? (
                                        <div className={classes.helperTextWarning}>
                                            <div className={classes.warningIcon}>
                                                <CustomIcons icon={'warning-orange'} />
                                            </div>
                                            <p> {applicationTokenExpiryWarning} </p>
                                        </div>
                                    ) : getApplicationTokenHelperText()}
                                    className={applicationTokenExpiryError === "" && applicationTokenExpiryWarning !== "" ?
                                    classes.textFieldWarning : classes.textField}
                                data-testid='application-token-expiry-time'
                            />
                        </Grid>
                    </Grid>
                    { (!Settings.devPortalIdPConfigurationFeatureEnabled || isUserGrantEnabled()) && (
                        <>
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                className={classes.advancedConfigRaw}
                            >
                                <Grid item md={4}
                                    className={clx({
                                        [classes.tableCellBorder]:
                                        isCredentialsAvailable,
                                        [classes.tableCellBorderNoKeys]:
                                            !isCredentialsAvailable,
                                    })}
                                >
                                    <Typography
                                        variant="body1"
                                    >
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.userAccessToken.expiryTime'
                                            defaultMessage='User access token expiry time'
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item md={8}
                                    className={clx({
                                        [classes.td2]:
                                        isCredentialsAvailable,
                                        [classes.td2NoKeys]:
                                            !isCredentialsAvailable,
                                    })}
                                >
                                    <TextField
                                        onChange={onUserAccessTokenExpiryChange}
                                        size="small"
                                        value={userAccessTokenExpiryTime}
                                        variant="outlined"
                                        error={userAccessTokenExpiryError !== ""}
                                        FormHelperTextProps={{
                                            className: classes.helperText
                                        }}
                                        helperText={userAccessTokenExpiryError !== "" ?
                                            (
                                                <div className={classes.helperTextError}>
                                                    <div className={classes.warningIcon}>
                                                        <CustomIcons icon={'warning'}/>
                                                    </div>
                                                    <p> {userAccessTokenExpiryError} </p>
                                                </div>
                                            ): userAccessTokenExpiryWarning !== "" ? (
                                                <div className={classes.helperTextWarning}>
                                                    <div className={classes.warningIcon}>
                                                        <CustomIcons icon={'warning-orange'}/>
                                                    </div>
                                                    <p> {userAccessTokenExpiryWarning} </p>
                                                </div>
                                            ): userTokenHelperText()}
                                        className={userAccessTokenExpiryError === "" && userAccessTokenExpiryWarning !== "" ?
                                            classes.textFieldWarning : classes.textField}
                                        data-testid='user-token-expiry-time'
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                className={classes.advancedConfigRaw}
                            >
                                <Grid item md={4}
                                    className={clx({
                                        [classes.tableCellBorder]:
                                        isCredentialsAvailable,
                                        [classes.tableCellBorderNoKeys]:
                                            !isCredentialsAvailable,
                                    })}
                                >
                                    <Typography
                                        variant="body1"
                                    >
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.refreshTokenExpiryTime'
                                            defaultMessage='Refresh token expiry time'
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item md={8}
                                    className={clx({
                                        [classes.td2]:
                                        isCredentialsAvailable,
                                        [classes.td2NoKeys]:
                                            !isCredentialsAvailable,
                                    })}
                                >
                                    <TextField
                                        onChange={onRefreshTokenExpiryChange}
                                        size="small"
                                        value={refreshTokenExpiryTime}
                                        variant="outlined"
                                        error={refreshTokenExpiryError !== ""}
                                        FormHelperTextProps={{
                                            className: classes.helperText
                                        }}
                                        helperText={refreshTokenExpiryError === "" ?
                                            getRefreshTokenHelperText() :
                                            <div className={classes.helperTextError}>
                                                <div className={classes.warningIcon}>
                                                    <CustomIcons icon={'warning'}/>
                                                </div>
                                                <p> {refreshTokenExpiryError} </p>
                                            </div>
                                        }
                                        className={classes.textField}
                                        data-testid='refresh-token-expiry-time'
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                className={classes.advancedConfigRaw}
                            >
                                <Grid item md={4}
                                    className={clx({
                                        [classes.tableCellBorder]:
                                        isCredentialsAvailable,
                                        [classes.tableCellBorderNoKeys]:
                                            !isCredentialsAvailable,
                                    })}
                                >
                                    <Typography
                                        variant="body1"
                                    >
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.idTokenExpiryTime'
                                            defaultMessage='ID token expiry time'
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item md={8}
                                    className={clx({
                                        [classes.td2]:
                                        isCredentialsAvailable,
                                        [classes.td2NoKeys]:
                                            !isCredentialsAvailable,
                                    })}
                                >
                                    <TextField
                                        onChange={onIdTokenExpiryChange}
                                        size="small"
                                        value={idTokenExpiryTime}
                                        variant="outlined"
                                        error={idTokenExpiryError !== ""}
                                        FormHelperTextProps={{
                                            className: classes.helperText
                                        }}
                                        helperText={idTokenExpiryError !== "" ?
                                            (
                                                <div className={classes.helperTextError}>
                                                    <div className={classes.warningIcon}>
                                                        <CustomIcons icon={'warning'} />
                                                    </div>
                                                    <p> {idTokenExpiryError} </p>
                                                </div>
                                            ) : idTokenExpiryWarning !== "" ? (
                                                <div className={classes.helperTextWarning}>
                                                    <div className={classes.warningIcon}>
                                                        <CustomIcons icon={'warning-orange'} />
                                                    </div>
                                                    <p> {idTokenExpiryWarning} </p>
                                                </div>
                                            ) : getIdTokenHelperText()}
                                        className={idTokenExpiryError === "" && idTokenExpiryWarning !== "" ?
                                            classes.textFieldWarning : classes.textField}
                                        data-testid='id-token-expiry-time'
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        {(ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, user.allowedScopes) && isCredentialsAvailable) &&
                            UpdateKeysButton()
                        }
                    </Grid>
                </Grid>
            </React.Fragment>
        );
    }

    const AccordianContent = () => {
        return (
            <React.Fragment>
                {!isCredentialsAvailable ?
                    <NoKeysAccordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize='small' />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                        >
                            <Grid item={true} xs={12} md={12} lg={12} className={classes.gridAccordion}>
                                <TableContainer>
                                    <Table className={classes.table} aria-label="table layout">
                                        <TableBody>
                                            <TableRow>
                                                { ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, user.allowedScopes) &&
                                                    <TableCell className={classes.tableCellBorder} align='right'>
                                                        {GenerateKeysButton()}
                                                    </TableCell>
                                                 }
                                                <TableCell
                                                    className={clx({
                                                        [classes.tableCellBorder]:
                                                        isCredentialsAvailable,
                                                        [classes.tableCellBorderNoKeys]:
                                                            !isCredentialsAvailable,
                                                    })}
                                                >
                                                    <Typography className={classNames(classes.rightAlignedSubTitle, 
                                                        {[classes.advancedConfigRightText]: ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, user.allowedScopes)})}>
                                                        <FormattedMessage 
                                                            id='application.productionKeys.oAuth.advancedConfigurations' 
                                                            defaultMessage='Advanced Configurations' />
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>
                            {AdvancedConfigContent()}
                        </AccordionDetails>
                    </NoKeysAccordion>
                    :
                    <WithKeysAccordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize='small' />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                        >
                            <Typography className={classes.subTitle}>
                                Advanced Configurations
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {AdvancedConfigContent()}
                        </AccordionDetails>
                    </WithKeysAccordion>
                }
            </React.Fragment >
        );
    }

    const UpdateKeysButton = () => {
        const updateDisabled = buttonDisabled || !selectedGrantTypes || selectedGrantTypes.length === 0;
        return (
            <Grid item>
                <Box>
                    <Button
                        disabled={updateDisabled}
                        variant="contained"
                        color="primary"
                        classes={{ root: classes.updateKeyConfigButton }}
                        onClick={handleUpdateClick}
                        data-testid='update-oauth-key'
                    >
                        <FormattedMessage
                            id='application.productionKeys.oAuth.updateKeyButton.label'
                            defaultMessage='Update'
                        />
                    </Button>
                </Box>
            </Grid>
        );
    }

    const onClickUserKeys = () => {
        setSelectedTab(1);
    }

    const managePermissionButtonTooltip = intl.formatMessage({
        id: 'application.productionKeys.oAuth.permissions.managePermissionButton.tooltip',
        defaultMessage: 'Manage permissions with Asgardeo '
    });

    const managePermissionButtonLabel = intl.formatMessage({
        id: 'application.productionKeys.oAuth.permissions.managePermissionButton.label',
        defaultMessage: 'Manage Permissions'
    });

    const onManagePermissionButtonClick = () => {
        if (asgardeoApplicationId) {
            window.open(
                `${Settings.asgardeoConsoleUrl}/t/${organizationHandle}/app/applications/${asgardeoApplicationId}#tab=application-roles`,
                "_blank"
            );
        } else {
            window.open(Settings.permissionAdminPortalUrl + '?' +
                Settings.permissionPortalOrgIdParam + '=' + selectedOrg.uuid, "_blank");
        }
    }

    const CredentialsAvailableSwitch = () => {
        if (isCredentialsAvailable) {
            return (
                <React.Fragment>
                    <TableContainer className={classes.credentialsWrapper}>
                        <Table className={classes.table} aria-label="table layout">
                            <TableBody>
                                <TableRow>
                                    <Typography className={classes.subTitle}>
                                        <FormattedMessage
                                            id='application.productionKeys.oAuth.keyAndSecret'
                                            defaultMessage='Credentials'
                                        />
                                    </Typography>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.tableCellBorder} align='left'>
                                        <Typography variant="body1" className={classes.keyLabel}>
                                            <FormattedMessage
                                                id='application.productionKeys.oAuth.consumerKey'
                                                defaultMessage='Consumer Key'
                                            />
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tdValueCell}>
                                        <CopyTextField className={classes.tdValueCell} textContent={consumerKey} testId='consumer-key-text' />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.tableCellBorder} align='left'>
                                        <Typography variant="body1" className={classes.keyLabel}>
                                            <FormattedMessage
                                                id='application.productionKeys.oAuth.consumerSecret'
                                                defaultMessage='Consumer Secret'
                                            />
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.td}>
                                        <PasswordCopyField
                                            className={classNames(classes.tdValueCell, classes.consumerSecretValue)}
                                            value={consumerSecret}
                                            id='consumer-secret-text'
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.tableCellBorder} align='left'>
                                        <Typography variant="body1" className={classes.keyLabel}>
                                            <FormattedMessage
                                                id='application.productionKeys.oAuth.testToken'
                                                defaultMessage='Test Token'
                                            />
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="left" className={classes.outlineButtonCell} colSpan={2}>
                                        <Box>
                                            {
                                                () => {
                                                    if (!isGenerateTokenDisabled) {
                                                        return (
                                                            <Box ml={1.5}>
                                                                <Box display="flex" flexDirection="row" mb={1}>
                                                                    <Box mr={1}>
                                                                        <GreyOutlinedButton
                                                                            className={classes.tokenBtnText}
                                                                            text={getTokenGenerateBtnText()}
                                                                            onClick={handleClickTokenOpen}
                                                                            startIcon={isTokenGenerating ? <CircularProgress size={11} color='primary' />
                                                                                : undefined}
                                                                            id='generate-token-btn'
                                                                            disabled={isGenerateTokenDisabled}
                                                                        />
                                                                        {tokenOpen &&
                                                                            <AccessTokenDialog
                                                                                token={token}
                                                                                tokenOpen={tokenOpen}
                                                                                onClose={handleClickTokenClose}
                                                                                handleClickGenerate={handleClickGenerate}
                                                                                selectedPermissions={selectedScopes}
                                                                                setPermissions={setPermissionsCallback}
                                                                                existingPermissions={subscriptionScopes}
                                                                                isTokenGenerating={isTokenGenerating}
                                                                                data-testid='generate-access-token-dialog-for-applications'
                                                                                generatedScopesForToken={generatedScopesForToken}
                                                                            />
                                                                        }
                                                                    </Box>
                                                                    <Box>
                                                                        <GreyOutlinedButton
                                                                            className={classes.tokenBtnText}
                                                                            text={getTokenGenerateCurlBtnText()}
                                                                            onClick={handleClickCurlOpen}
                                                                            id='curl-to-generate-token-btn'
                                                                            disabled={isGenerateTokenDisabled}
                                                                        />
                                                                        {curlOpen &&
                                                                            <CurlDialog
                                                                                curlOpen={curlOpen}
                                                                                base64Encoded={base64Encoded}
                                                                                tokenEndpoint={getEndpoints().tokenEndpoint}
                                                                                externalIDPToken=''
                                                                                onClose={handleClickCurlClose}
                                                                            />
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                                {!(isAsgardeoKeyManagerAvailable() && isResidentKeyManagerNotUsed()) &&
                                                                <Box>
                                                                    <InfoBanner
                                                                        message={
                                                                            <Box>
                                                                                <Typography variant='subtitle2'
                                                                                            className={classes.tokenDocInfoText}>
                                                                                    <FormattedMessage
                                                                                        id="application.productionKeys.oAuth.tokenGeneration.documentationInfo"
                                                                                        defaultMessage="This is a test access token. Please refer to the {documentation} to learn how to generate a production access token."
                                                                                        values={{
                                                                                            documentation: (
                                                                                                <Link
                                                                                                    href="https://wso2.com/choreo/docs/developer-portal/manage-application/#generate-keys"
                                                                                                    target="_blank">
                                                                                                    <FormattedMessage
                                                                                                        id='application.productionKeys.oAuth.tokenGeneration.documentation.text'
                                                                                                        defaultMessage='documentation'
                                                                                                    />
                                                                                                </Link>
                                                                                            )
                                                                                        }}
                                                                                    />
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                        testId='test-token-doc-info'
                                                                    />
                                                                </Box>
                                                                }
                                                            </Box>
                                                        )
                                                    } else if (!(isExternalIDPsConfigured() && isCredentialsAvailable)) {
                                                        return (
                                                            <Box ml={1.5}>
                                                                <WarningBanner
                                                                    message={
                                                                        <Box>
                                                                            <Typography variant='subtitle2' className={classes.tokenDocInfoText}>
                                                                                <FormattedMessage
                                                                                    id="application.productionKeys.oAuth.tokenGeneration.documentationWarning.noExternalIdp"
                                                                                    defaultMessage="Client credentials grant type is disabled. Refer to the {documentation} to learn how to generate a production access token."
                                                                                    values={{
                                                                                        documentation: (
                                                                                            <Link href="https://wso2.com/choreo/docs/developer-portal/manage-application/#generate-keys" target="_blank">
                                                                                                <FormattedMessage
                                                                                                    id='application.productionKeys.oAuth.tokenGeneration.documentation.text'
                                                                                                    defaultMessage='documentation'
                                                                                                />
                                                                                            </Link>
                                                                                        )
                                                                                    }}
                                                                                />
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    testId='test-token-doc-warning'
                                                                />   
                                                            </Box>  
                                                        )
                                                    } else {
                                                        return (
                                                            <Box ml={1.5}>
                                                                <WarningBanner
                                                                    message={
                                                                        <Box>
                                                                            <Typography variant='subtitle2' className={classes.tokenDocInfoText}>
                                                                                <FormattedMessage
                                                                                    id="application.productionKeys.oAuth.tokenGeneration.documentationWarning"
                                                                                    defaultMessage="Client credentials grant type is disabled. Please {tryUserKeys} to generate a production access token."
                                                                                    values={{
                                                                                        tryUserKeys: (
                                                                                            <Link onClick={onClickUserKeys}>
                                                                                                <FormattedMessage
                                                                                                    id="application.productionKeys.oAuth.tokenGeneration.documentationWarning.tryUserKeys"
                                                                                                    defaultMessage="try user keys"
                                                                                                />
                                                                                        </Link>
                                                                                        )
                                                                                    }}
                                                                                />
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    testId='test-token-user-keys-warning'
                                                                />   
                                                            </Box>                                       
                                                        )
                                                    }    
                                                }
                                            }
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                
                                {Settings.permissionAdminPortalEnabled && selectedIdp?.name === "default" && isAsgardeoKeyManagerAvailable() && (
                                    <>
                                        <TableRow>
                                            <TableCell className={classes.emptyTableCell} colSpan={2}>
                                                <Divider light />
                                            </TableCell>
                                        </TableRow>

                                        <TableRow>
                                            <TableCell className={classes.borderlessTableCell} colSpan={2}>
                                                <Box display="flex" flexDirection="row">
                                                    <Box mr={2} alignSelf="center">
                                                        <Typography className={classes.inlineSubTitle}>
                                                            <FormattedMessage
                                                                id='application.productionKeys.oAuth.permissions'
                                                                defaultMessage='Permissions'
                                                            />
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                            <Tooltip
                                                                title={managePermissionButtonTooltip}
                                                                interactive={true}
                                                                placement='top'
                                                            >
                                                                <Box>
                                                                    <Button
                                                                        onClick={onManagePermissionButtonClick}
                                                                        className={classNames(classes.permissionBtn)}
                                                                        data-testid="appliation-permission-btn"
                                                                        startIcon={
                                                                            <CustomIcons
                                                                                icon={isManagePermissionButtonDisabled ?
                                                                                    "open-link-in-new-tab-disabled" :
                                                                                    "open-link-in-new-tab"}
                                                                            />}
                                                                        disabled={isManagePermissionButtonDisabled}
                                                                        variant='text'
                                                                    >
                                                                        <Typography variant="body1">
                                                                            {managePermissionButtonLabel}
                                                                        </Typography>
                                                                    </Button>
                                                                </Box>
                                                            </Tooltip>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                                <TableRow>
                                    <TableCell className={classes.emptyTableCell} colSpan={2}>
                                        <Divider light />
                                    </TableCell>
                                </TableRow>
                                <TokenEndpointList />
                                <TableRow>
                                    <TableCell className={classes.emptyTableCell} colSpan={2}>
                                        <Divider light />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </React.Fragment>
            );
        } else {
            return (
                <React.Fragment>
                    { ((Settings.devPortalIdPConfigurationFeatureEnabled && idpList?.length > 0) || availableExternalIdps().length > 0) && <IdpList /> }
                    <Box mt={2}>
                        <NoKeysHelpText/>
                    </Box>
                </React.Fragment >
            );
        }
    };

    const ApplicationKeysContent = () => {
        return (
            <Grid container={true} spacing={3} className={classes.grid}>
                { isKeyMapped() ?
                    <>
                        <Grid item={true} xs={12} md={12} lg={11} className={classes.grid}>
                            <Box my={2}>
                                {ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, user.allowedScopes) && AddIdpClientForm()}
                            </Box>
                            <Box my={2}>
                                <TableContainer>
                                    <Table className={classes.table} aria-label="endpoints table">
                                        <TableBody>
                                            <TokenEndpointList />
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Grid>
                    </> : 
                    <>
                       <Grid item={true} xs={12} md={12} lg={11}>
                            {CredentialsAvailableSwitch()}
                        </Grid>
                    </>
                }
                <Grid item={true} xs={12} md={12} lg={11}>
                    { !currentKey && selectedIdp && selectedIdp?.type !== 'default' && selectedIdp?.type !== APPDEV_STS_KEY_MANAGER_TYPE &&
                        selectedIdp?.name !== defaultAsgardeoKeyManagerName ? 
                        ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, user.allowedScopes) &&  AddIdpClientForm() :  
                        !isKeyMapped() && AccordianContent()
                        }
                </Grid>
            </Grid>
        );
    };

    const OauthContent = () => {
        if (isLoading) {
            return (
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div>
            );
        } else {
            return (
                <Container maxWidth="lg" className={classes.container} classes={{ root: classes.componentPadding }}>
                    {isAsgardeoKeyManagerAvailable() && isCredentialsAvailable && !isKeyFromAsgardeoKM && !isKeyMapped() && appDevSTSList?.length === 0 &&
                    <WarningBanner
                        message={
                            <Box>
                                <Typography>
                                    <FormattedMessage
                                        id="components.app..appProductionKeys.oAuth.index.keyGeneration.oldKeyManagerWarning"
                                        defaultMessage="This application still using the Resident Key Manager. Please create a
                                    new application and migrate to that application."
                                    />
                                </Typography>
                            </Box>
                        }
                        testId='resident-key-manager-usage-warning'
                    />
                    }
                    {!isAsgardeoKeyManagerAvailable() && isExternalIDPsConfigured() ? (
                        <>
                            <Tabs
                                value={selectedTab}
                                onChange={handleTabChange}
                                classes={{ root: classes.tabRoot, indicator: classes.tabIndicator }}
                                data-testid='tab-view'
                            >
                                { Settings.apiKeyFeatureEnabled && apiKeys.length > 0 && (
                                    <Tab
                                        disableRipple={true}
                                        disableFocusRipple={true}
                                        label={intl.formatMessage({
                                            id: 'components.app.appProductionKeys.oAuth.title.apiKeys',
                                            defaultMessage: 'API Keys'
                                        })}
                                        classes={{ wrapper: classes.tabTitleText, root: classes.tab, selected: classes.selectedTab }}
                                        data-testid='api-keys-tab' />
                                )}
                                <Tab
                                    disableRipple={true}
                                    disableFocusRipple={true}
                                    label="Application Keys"
                                    classes={{ wrapper: classes.tabTitleText, root: classes.tab, selected: classes.selectedTab }}
                                    data-testid='application-tab' />
                                {!isAsgardeoKeyManagerAvailable() && isExternalIDPsConfigured() && isTokenExchangeSelected && isCredentialsAvailable && (
                                    <Tab
                                        disableRipple={true}
                                        disableFocusRipple={true}
                                        label="User Keys"
                                        classes={{ wrapper: classes.tabTitleText, root: classes.tab, selected: classes.selectedTab }}
                                        data-testid='identity-tab' />
                                )}
                                {!isAsgardeoKeyManagerAvailable() && isExternalIDPsConfigured() && isTokenExchangeSelected && !isCredentialsAvailable && (
                                    <Tooltip title={getUserKeysTooltip()} interactive={true}>
                                        <span>
                                            <Tab
                                                disableRipple={true}
                                                disableFocusRipple={true}
                                                label="User Keys"
                                                classes={{ wrapper: classes.tabTitleText, root: classes.tab, selected: classes.selectedTab }}
                                                data-testid='identity-tab'
                                                disabled={true} />
                                        </span>
                                    </Tooltip>
                                )}

                            </Tabs>
                            { Settings.apiKeyFeatureEnabled && apiKeys.length > 0 ? (
                                <>
                                    <TabPanel selectedIndex={selectedTab} index={0}>
                                        <Loader />
                                        <ApiKeysList onClose={onApiKeyConfirmationClose} viewType={ViewType.ApplicationView} />
                                    </TabPanel>
                                    <TabPanel selectedIndex={selectedTab} index={1}>
                                        {ApplicationKeysContent()}
                                    </TabPanel>
                                    {
                                        !isAsgardeoKeyManagerAvailable() && isExternalIDPsConfigured() && (
                                            <TabPanel selectedIndex={selectedTab} index={2}>
                                                {isCredentialsAvailable && isTokenExchangeSelected &&
                                                    <ExternalIDP />
                                                }

                                            </TabPanel>
                                        )
                                    }
                                </>
                            ) : (
                                <>
                                    <TabPanel selectedIndex={selectedTab} index={0}>
                                        {ApplicationKeysContent()}
                                    </TabPanel>
                                    {
                                        !isAsgardeoKeyManagerAvailable() && isExternalIDPsConfigured() && (
                                            <TabPanel selectedIndex={selectedTab} index={1}>
                                                {isCredentialsAvailable && isTokenExchangeSelected &&
                                                    <ExternalIDP />
                                                }
                                            </TabPanel>
                                        )
                                    }
                                </>
                                )
                            }
                        </>
                    ) : (
                        <>
                            { Settings.apiKeyFeatureEnabled && apiKeys.length > 0 ? (
                                <>
                                    <Tabs
                                        value={selectedTab}
                                        onChange={handleTabChange}
                                        classes={{ root: classes.tabRoot, indicator: classes.tabIndicator }}
                                        data-testid='tab-view'
                                    >
                                        <Tab
                                            disableRipple={true}
                                            disableFocusRipple={true}
                                            label={intl.formatMessage({
                                                id: 'components.app.appProductionKeys.oAuth.title.apiKeys',
                                                defaultMessage: 'API Keys'
                                            })}
                                            classes={{ wrapper: classes.tabTitleText, root: classes.tab, selected: classes.selectedTab }}
                                            data-testid='api-keys-tab' />
                                        <Tab
                                            disableRipple={true}
                                            disableFocusRipple={true}
                                            label="Application Keys"
                                            classes={{ wrapper: classes.tabTitleText, root: classes.tab, selected: classes.selectedTab }}
                                            data-testid='application-tab' />    
                                    </Tabs>
                                    <TabPanel selectedIndex={selectedTab} index={0}>
                                        <Loader />
                                        <ApiKeysList onClose={onApiKeyConfirmationClose} viewType={ViewType.ApplicationView} />
                                    </TabPanel>
                                    <TabPanel selectedIndex={selectedTab} index={1}>
                                        {ApplicationKeysContent()}
                                    </TabPanel>
                                </>
                            ) : (
                                <>
                                    <Typography className={classes.pageTitle}>
                                        <FormattedMessage
                                            id='components.app.appProductionKeys.oAuth.title.applicationKeys'
                                            defaultMessage='Application Keys'
                                        />
                                    </Typography>
                                    {ApplicationKeysContent()}
                                </>
                            )}
                        </>
                    )}
                    
                </Container >
            )
        }
    }

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <AppInfoBar />
            {OauthContent()}
        </main>
    );
}

const mapDispatchToProps = {
    dispatchGenerateAppKey: createAppKeys,
    dispatchFetchAppKeys: fetchAppKeys,
    dispatchUpdateAppKey: updateApplicationKey,
    dispatchGenerateApplicationToken: generateApplicationToken,
    dispatchFetchIDPs: fetchIDPs,
    dispatchFetchSubscriptionsList: fetchSubscriptions,
    dispatchSetApplicationKeyMap: mapApplicationKeys,
    dispatchFetchApiKeys: fetchApiKeysList,
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        applicationKeysReducer: state.applicationKeysReducer,
        userReducer: state.userReducer,
        idpReducer: state.idpReducer,
        subscriptionReducer: state.subscriptionsReducer,
        apiKeysReducer: state.apiKeysReducer,
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(OauthKey);
